import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import BlockIcon from '@mui/icons-material/Block';
import ChatIcon from '@mui/icons-material/Chat';
import DoneIcon from '@mui/icons-material/Done';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import withComponentAuth from '../../hoc/withComponentAuth';
import { debounce } from '../../utils/helper';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';
import { AddPartnerLeadDialog } from './AddPartnerLeadDialog';
import {
  PartnerLead,
  PartnerLeadProductType,
  PartnerLeadStatus,
  getPartnerLeadStats,
  getPartnerLeads,
} from '../../services/channelPartners';

export const LEAD_STATUSES: PartnerLeadStatus[] = [
  'SUBMITTED',
  'UNDER_REVIEW',
  'CONTACTED',
  'CONVERTED',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
];

const PRODUCT_TYPES: PartnerLeadProductType[] = ['air_conditioning', 'heat_pump', 'boiler', 'other'];

export const leadStatusMeta: Record<PartnerLeadStatus, { label: string; color: string; bg: string }> = {
  SUBMITTED: { label: 'Submitted', color: '#60a5fa', bg: '#1d4ed833' },
  UNDER_REVIEW: { label: 'Under review', color: '#f59e0b', bg: '#f59e0b22' },
  CONTACTED: { label: 'Contacted', color: '#22c55e', bg: '#22c55e22' },
  CONVERTED: { label: 'Converted', color: '#0BE600', bg: '#0BE60022' },
  REJECTED: { label: 'Rejected', color: '#ef4444', bg: '#ef444422' },
  CANCELLED: { label: 'Cancelled', color: '#f97316', bg: '#f9731622' },
  EXPIRED: { label: 'Expired', color: '#94a3b8', bg: '#64748b22' },
};

export const pretty = (value?: string | null) =>
  value ? value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'N/A';

export const LeadStatusChip = ({ status }: { status: PartnerLeadStatus }) => {
  const meta = leadStatusMeta[status];
  return <Chip label={meta.label} size="small" sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 700 }} />;
};

const StatCard = ({ label, value, icon, color }: { label: string; value: number; icon: React.ReactNode; color: string }) => (
  <Paper sx={{ p: 2, minWidth: 150, flex: 1, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box sx={{ width: 38, height: 38, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}20`, color }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color="#fff" lineHeight={1.1}>{value}</Typography>
        <Typography variant="caption" color="#64748b">{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

const formatAddress = (lead: PartnerLead) =>
  [lead.address?.line1, lead.address?.line2, lead.address?.town, lead.address?.county, lead.address?.postcode]
    .filter(Boolean)
    .join(', ');

const PartnerLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<PartnerLead[]>([]);
  const [stats, setStats] = useState<Record<PartnerLeadStatus, number> | null>(null);
  const [totalStats, setTotalStats] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PartnerLeadStatus | ''>('');
  const [productType, setProductType] = useState<PartnerLeadProductType | ''>('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [addLeadOpen, setAddLeadOpen] = useState(false);

  useUrlSearchQuery((query: string) => {
    setSearch(query);
    setPage(1);
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [list, nextStats] = await Promise.all([
        getPartnerLeads({ page, limit: rowsPerPage, search, status, productType }),
        getPartnerLeadStats(),
      ]);
      setLeads(list.data || []);
      setTotal(list.pagination.total || 0);
      setStats(nextStats.byStatus);
      setTotalStats(nextStats.total || 0);
    } catch (error) {
      console.error('Failed to load partner leads:', error);
      setLeads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, status, productType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = debounce((value: string) => {
    setSearch(value);
    setPage(1);
  }, 500);

  const statValue = (item: PartnerLeadStatus) => stats?.[item] || 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <SmartFilter
        title="Partner Leads"
        createFilter={['SEARCH']}
        onSearch={handleSearch}
        extraActions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddLeadOpen(true)}
            sx={{
              backgroundColor: '#7B1FA2',
              '&:hover': { backgroundColor: '#5E167A' },
              textTransform: 'none',
              borderRadius: '10px',
            }}
          >
            Add Lead
          </Button>
        }
      />
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <StatCard label="Total" value={totalStats} icon={<GroupsIcon fontSize="small" />} color="#60a5fa" />
          <StatCard label="Submitted" value={statValue('SUBMITTED')} icon={<HourglassTopIcon fontSize="small" />} color="#60a5fa" />
          <StatCard label="Under review" value={statValue('UNDER_REVIEW')} icon={<AssignmentTurnedInIcon fontSize="small" />} color="#f59e0b" />
          <StatCard label="Contacted" value={statValue('CONTACTED')} icon={<ChatIcon fontSize="small" />} color="#22c55e" />
          <StatCard label="Converted" value={statValue('CONVERTED')} icon={<DoneIcon fontSize="small" />} color="#0BE600" />
          <StatCard label="Closed" value={statValue('REJECTED') + statValue('CANCELLED') + statValue('EXPIRED')} icon={<BlockIcon fontSize="small" />} color="#ef4444" />
        </Box>

        <Paper sx={{ p: 1.5, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1.5}>
            <Chip label="All" clickable onClick={() => { setStatus(''); setPage(1); }} sx={{ bgcolor: status === '' ? '#7B1FA2' : '#242424', color: '#fff' }} />
            {LEAD_STATUSES.map((item) => (
              <Chip key={item} label={leadStatusMeta[item].label} clickable onClick={() => { setStatus(item); setPage(1); }} sx={{ bgcolor: status === item ? leadStatusMeta[item].bg : '#242424', color: status === item ? leadStatusMeta[item].color : '#94a3b8', fontWeight: 600 }} />
            ))}
            <FormControl size="small" sx={{ minWidth: 190, ml: { xs: 0, md: 'auto' } }}>
              <InputLabel sx={{ color: '#94a3b8' }}>Product</InputLabel>
              <Select
                value={productType}
                label="Product"
                onChange={(event) => { setProductType(event.target.value as PartnerLeadProductType | ''); setPage(1); }}
                sx={{ color: '#e2e8f0', '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' }, '.MuiSvgIcon-root': { color: '#94a3b8' } }}
              >
                <MenuItem value="">All products</MenuItem>
                {PRODUCT_TYPES.map((item) => <MenuItem key={item} value={item}>{pretty(item)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {['Lead ID', 'Customer', 'Phone', 'Product / Job', 'Partner', 'Status', 'Created', 'Action'].map((head) => (
                    <TableCell key={head} sx={{ color: '#64748b', borderColor: '#242424', whiteSpace: 'nowrap' }} align={head === 'Action' ? 'right' : 'left'}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} sx={{ color: '#94a3b8', borderColor: '#242424' }}>Loading partner leads...</TableCell></TableRow>
                ) : leads.length === 0 ? (
                  <TableRow><TableCell colSpan={8} sx={{ color: '#94a3b8', borderColor: '#242424' }}>No partner leads found</TableCell></TableRow>
                ) : leads.map((lead) => (
                  <TableRow key={lead._id} hover onClick={() => navigate(`/channel-partners/leads/${lead._id}`)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                    <TableCell sx={{ color: '#e2e8f0', borderColor: '#242424', whiteSpace: 'nowrap', fontWeight: 700 }}>{lead.channelPartnerLeadId}</TableCell>
                    <TableCell sx={{ borderColor: '#242424', minWidth: 220 }}>
                      <Typography
                        onClick={(e) => { e.stopPropagation(); navigate(`/channel-partners/leads/${lead._id}`); }}
                        sx={{ color: '#c084fc', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {lead.customerName}
                      </Typography>
                      <Typography color="#64748b" fontSize={12} noWrap>{formatAddress(lead)}</Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424', whiteSpace: 'nowrap' }}>{lead.customerPhoneNumber}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424', whiteSpace: 'nowrap' }}>{pretty(lead.productType)} / {pretty(lead.jobType)}</TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424', whiteSpace: 'nowrap' }}>
                      {lead.channelPartner?._id ? (
                        <Typography
                          onClick={(e) => { e.stopPropagation(); navigate(`/channel-partners/${lead.channelPartner?._id}`); }}
                          sx={{ color: '#c084fc', fontSize: 14, cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {lead.channelPartner.fullName || lead.channelPartner.phoneNumber}
                        </Typography>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ borderColor: '#242424' }}><LeadStatusChip status={lead.status} /></TableCell>
                    <TableCell sx={{ color: '#94a3b8', borderColor: '#242424', whiteSpace: 'nowrap' }}>{dayjs(lead.createdAt).format('DD MMM YYYY')}</TableCell>
                    <TableCell align="right" sx={{ borderColor: '#242424' }}>
                      <Button size="small" startIcon={<VisibilityIcon />} onClick={(e) => { e.stopPropagation(); navigate(`/channel-partners/leads/${lead._id}`); }}>View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Pagination count={total} page={page - 1} rowsPerPage={rowsPerPage} onPageChange={(_: any, newPage: number) => setPage(newPage + 1)} onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(1); }} />
        </Paper>
      </Box>

      <AddPartnerLeadDialog
        open={addLeadOpen}
        onClose={() => setAddLeadOpen(false)}
        onCreated={() => {
          setPage(1);
          fetchData();
        }}
      />
    </Box>
  );
};

export default withComponentAuth(PartnerLeads);